import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Patient } from '../../types';
import { Users, UserPlus, Search, Calendar, Clock, CheckCircle, AlertCircle, Stethoscope, Baby, FileText, ArrowRight } from 'lucide-react';
import { PatientReports } from './PatientReports';
import { PatientReviewDueList } from './PatientReviewDueList';
interface Props {}
export const ReceptionDashboard: React.FC<Props> = () => {
  const {
    patients,
    addPatient,
    encounters,
    createEncounter,
    currentUser,
    getPatientEncounters,
    addMaternityFile,
    maternityFiles
  } = useData();
  const [activeTab, setActiveTab] = useState<'register' | 'queue' | 'reports' | 'reviewDue' | 'maternity'>('register');
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    sex: 'Male' as 'Male' | 'Female',
    age: '',
    maritalStatus: 'Single' as 'Married' | 'Single',
    address: '',
    contact: '',
    occupation: '',
    nextOfKin: '',
    nokContact: '',
    nokRelationship: '',
    allergies: '',
    hivStatus: 'Unknown' as 'Positive' | 'Negative' | 'Unknown',
    isVitalsCompleted: false
  });
  // Maternity Form State
  const [maternityForm, setMaternityForm] = useState({
    firstName: '',
    surname: '',
    otherNames: '',
    age: '',
    religion: '',
    churchAttended: '',
    residentialAddress: '',
    locationLandmark: '',
    phone: '',
    nokName: '',
    nokPhone: '',
    nokRelationship: '',
    spouseFirstName: '',
    spouseSurname: '',
    spouseOtherNames: '',
    spouseOccupation: '',
    spouseContact: ''
  });
  // Computed properties
  const activeEncounters = useMemo(() => {
    const now = Date.now();
    const upper = now + 24 * 60 * 60 * 1000;
    return encounters.filter(e => {
      if (!e.reviewDate) return false;
      if (e.reviewStatus === 'Cancelled') return false;
      const t = new Date(e.reviewDate).getTime();
      if (Number.isNaN(t)) return false;
      return t <= upper; // includes overdue (t < now) AND future-within-24h
    }).length;
  }, [encounters]);
  const filteredPatients = patients.filter(p => p.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || p.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || p.bmcFileNumber.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    const newPatient = addPatient({
      ...formData,
      age: parseInt(formData.age) || 0
    });
    // Auto create encounter and send to nursing
    createEncounter(newPatient.id);
    // Reset form and show success
    setFormData({
      firstName: '',
      lastName: '',
      sex: 'Male',
      age: '',
      maritalStatus: 'Single',
      address: '',
      contact: '',
      occupation: '',
      nextOfKin: '',
      nokContact: '',
      nokRelationship: '',
      allergies: '',
      hivStatus: 'Unknown'
    });
    alert(`Patient registered successfully! BMC Number: ${newPatient.bmcFileNumber}. Sent to Nursing.`);
    setActiveTab('search');
    setSearchQuery(newPatient.bmcFileNumber);
  };
  const handleRegisterMaternity = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const prefix = `MAT-${yy}${mm}`;
    const monthCount = maternityFiles.filter(m => m.bmcFileNumber.startsWith(prefix)).length;
    const sequence = String(monthCount + 1).padStart(3, '0');
    const bmcFileNumber = `${prefix}${sequence}`;
    addMaternityFile({
      bmcFileNumber,
      createdAt: new Date().toISOString(),
      createdBy: currentUser.id,
      stage: 'NURSING',
      patient: {
        firstName: maternityForm.firstName,
        surname: maternityForm.surname,
        otherNames: maternityForm.otherNames,
        age: maternityForm.age,
        religion: maternityForm.religion,
        churchAttended: maternityForm.churchAttended,
        residentialAddress: maternityForm.residentialAddress,
        locationLandmark: maternityForm.locationLandmark,
        phone: maternityForm.phone,
        nextOfKin: {
          name: maternityForm.nokName,
          phone: maternityForm.nokPhone,
          relationship: maternityForm.nokRelationship
        }
      },
      spouse: {
        firstName: maternityForm.spouseFirstName,
        surname: maternityForm.spouseSurname,
        otherNames: maternityForm.spouseOtherNames,
        occupation: maternityForm.spouseOccupation,
        contact: maternityForm.spouseContact
      },
      admission: {
        date: '',
        time: ''
      },
      generalExam: {
        temp: '',
        pulse: '',
        respiration: '',
        bp: '',
        oedema: '',
        anemia: '',
        cardiovascularSystem: '',
        respiratorySystem: '',
        centralNervousSystem: '',
        breasts: '',
        varicosities: '',
        vaginalDischarge: '',
        otherAbnormalities: '',
        msUrine: {
          sg: '',
          albumen: '',
          acetone: '',
          sugar: ''
        },
        hb: '',
        abo: '',
        rh: ''
      },
      presentPregnancy: {
        lmp: '',
        quickening: '',
        edd: '',
        gestationAge: '',
        ancSummaryFirstBp: '',
        ancSummaryAtWeeks: '',
        syphilisTest: {
          date: '',
          result: '',
          treatment: ''
        },
        hpTest: {
          date: '',
          result: ''
        },
        pelvicAssessment: '',
        abnormalities: ''
      },
      labourObservations: [],
      delivery: {
        laceration: '',
        anterior: '',
        posterior: '',
        none: '',
        repaired: '',
        membranes: '',
        membranesDate: '',
        membranesTime: '',
        episiotomy: '',
        episiotomyRepaired: '',
        maternalARV: '',
        arvTypes: '',
        uterineMassage: '',
        motherOnHAART: '',
        cytotec: '',
        syntometrine: '',
        firstStageBeganDate: '',
        firstStageBeganTime: '',
        placentaDelivery: '',
        completeDilationDate: '',
        completeDilationTime: '',
        placentaDeliveredDate: '',
        placentaDeliveredTime: '',
        abnormalityAtDilation: '',
        abnormalityAtDilationDesc: '',
        postpartumHemorrhage: '',
        deliveryMethod: '',
        ebl: '',
        abnormalityAtDelivery: '',
        abnormalityAtDeliveryDesc: '',
        complications: '',
        deliverySummary: []
      },
      newbornEvaluation: {
        ega: '',
        alive: '',
        neonatalDeath: '',
        causeOfDeath: '',
        apgar1: '',
        apgar5: '',
        apgar10: '',
        generalExam: '',
        congenitalMalformation: '',
        congenitalMalformationDesc: '',
        infantARVs: '',
        infantFeeding: '',
        resuscitationMethods: '',
        crownHeelLength: '',
        headCircumference: '',
        skinJaundice: '',
        skinOther: '',
        fontanelles: '',
        eyes: '',
        eyesTreatment: '',
        mouth: '',
        neck: '',
        chest: '',
        abdomen: '',
        umbilicus: '',
        genitalia: '',
        urine: '',
        anus: '',
        meconium: '',
        upperLimbs: '',
        erbParalysis: '',
        lowerLimbs: '',
        ortolaniSign: '',
        back: '',
        reflexesGrasp: '',
        reflexesMoro: '',
        reflexesSucking: '',
        progressNotes: ''
      },
      postDeliveryObservations: [],
      puerperiumNotes: []
    });
    setMaternityForm({
      firstName: '',
      surname: '',
      otherNames: '',
      age: '',
      religion: '',
      churchAttended: '',
      residentialAddress: '',
      locationLandmark: '',
      phone: '',
      nokName: '',
      nokPhone: '',
      nokRelationship: '',
      spouseFirstName: '',
      spouseSurname: '',
      spouseOtherNames: '',
      spouseOccupation: '',
      spouseContact: ''
    });
    alert(`Maternity patient registered successfully. File Number: ${bmcFileNumber}. File forwarded to Nursing.`);
  };
  const handleAddToQueue = (patientId: string) => {
    createEncounter(patientId);
    alert('Patient added to queue successfully.');
  };
  return <div className="space-y-6" data-id="element-5509">
      <div className="flex justify-between items-center" data-id="element-5510">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-5511">
          Reception Dashboard
        </h1>
        <nav className="flex space-x-2" data-id="element-5512">
          <button onClick={() => setActiveTab('register')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'register' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5513">
            <UserPlus className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-5514" />
            Register New
          </button>
          <button onClick={() => setActiveTab('queue')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'queue' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5515">
            <Users className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-5516" />
            Queue
          </button>
          <button onClick={() => setActiveTab('reports')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'reports' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5517">
            <FileText className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-5518" />
            Patient Reports
          </button>
          <button onClick={() => setActiveTab('reviewDue')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'reviewDue' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5519">
            Review Due
          </button>
          <button onClick={() => setActiveTab('maternity')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'maternity' ? 'bg-white text-pink-600 border-t-2 border-pink-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-5520">
            <Baby className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-5521" />
            Maternity Registration
          </button>
        </nav>
      </div>

      {activeTab === 'register' && <Card title="Register New Patient" data-id="element-5522">
          <form onSubmit={handleRegister} className="space-y-6" data-id="element-5523">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5524">
              <Input label="First Name" required value={formData.firstName} onChange={e => setFormData({
            ...formData,
            firstName: e.target.value
          })} data-id="element-5525" />
              <Input label="Last Name" required value={formData.lastName} onChange={e => setFormData({
            ...formData,
            lastName: e.target.value
          })} data-id="element-5526" />
              <Input label="Age" type="number" required value={formData.age} onChange={e => setFormData({
            ...formData,
            age: e.target.value
          })} data-id="element-5527" />

              <Select label="Sex" options={[{
            value: 'Male',
            label: 'Male'
          }, {
            value: 'Female',
            label: 'Female'
          }]} required value={formData.sex} onChange={e => setFormData({
            ...formData,
            sex: e.target.value as any
          })} data-id="element-5528" />
              <Select label="Marital Status" options={[{
            value: 'Single',
            label: 'Single'
          }, {
            value: 'Married',
            label: 'Married'
          }]} required value={formData.maritalStatus} onChange={e => setFormData({
            ...formData,
            maritalStatus: e.target.value as any
          })} data-id="element-5529" />
              <Input label="Contact Number" required value={formData.contact} onChange={e => setFormData({
            ...formData,
            contact: e.target.value
          })} data-id="element-5530" />

              <Input label="Home Address" className="md:col-span-2" required value={formData.address} onChange={e => setFormData({
            ...formData,
            address: e.target.value
          })} data-id="element-5531" />
              <Input label="Occupation" value={formData.occupation} onChange={e => setFormData({
            ...formData,
            occupation: e.target.value
          })} data-id="element-5532" />
            </div>

            <div className="border-t border-gray-200 pt-4" data-id="element-5533">
              <h4 className="text-md font-medium text-gray-900 mb-4" data-id="element-5534">
                Emergency Contact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5535">
                <Input label="Next of Kin Name" required value={formData.nextOfKin} onChange={e => setFormData({
              ...formData,
              nextOfKin: e.target.value
            })} data-id="element-5536" />
                <Input label="Relationship" required value={formData.nokRelationship} onChange={e => setFormData({
              ...formData,
              nokRelationship: e.target.value
            })} data-id="element-5537" />
                <Input label="Contact Number" required value={formData.nokContact} onChange={e => setFormData({
              ...formData,
              nokContact: e.target.value
            })} data-id="element-5538" />
              </div>
            </div>

            <div className="flex justify-end pt-4" data-id="element-5539">
              <Button type="submit" size="lg" data-id="element-5540">
                Register & Send to Nursing
              </Button>
            </div>
          </form>
        </Card>}

      {activeTab === 'queue' && <div className="space-y-6" data-id="element-5541">
          <Card data-id="element-5542">
            <div className="relative" data-id="element-5543">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5544">
                <Search className="h-5 w-5 text-gray-400" data-id="element-5545" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name or BMC number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-5546" />
            </div>
          </Card>

          {searchQuery && !selectedPatient && <Card noPadding data-id="element-5547">
              <ul className="divide-y divide-gray-200" data-id="element-5548">
                {filteredPatients.length > 0 ? filteredPatients.map(patient => <li key={patient.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedPatient(patient)} data-id="element-5549">
                      <div data-id="element-5550">
                        <p className="text-sm font-medium text-blue-600" data-id="element-5551">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-5552">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-5553">
                          {patient.age} yrs • {patient.sex} • {patient.contact}
                        </p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-5554" />
                    </li>) : <li className="p-8 text-center text-gray-500" data-id="element-5555">
                    No patients found matching "{searchQuery}"
                  </li>}
              </ul>
            </Card>}

          {selectedPatient && <div className="space-y-6" data-id="element-5556">
              <Button variant="ghost" onClick={() => setSelectedPatient(null)} className="mb-2" data-id="element-5557">
                ← Back to search results
              </Button>

              <Card title="Patient Details" action={<Button onClick={() => handleAddToQueue(selectedPatient.id)} data-id="element-5559">
                    Add to Queue
                  </Button>} data-id="element-5558">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-5560">
                  <div data-id="element-5561">
                    <p className="text-sm text-gray-500" data-id="element-5562">BMC Number</p>
                    <p className="font-medium" data-id="element-5563">
                      {selectedPatient.bmcFileNumber}
                    </p>
                  </div>
                  <div data-id="element-5564">
                    <p className="text-sm text-gray-500" data-id="element-5565">Full Name</p>
                    <p className="font-medium" data-id="element-5566">
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </p>
                  </div>
                  <div data-id="element-5567">
                    <p className="text-sm text-gray-500" data-id="element-5568">Age / Sex</p>
                    <p className="font-medium" data-id="element-5569">
                      {selectedPatient.age} / {selectedPatient.sex}
                    </p>
                  </div>
                  <div data-id="element-5570">
                    <p className="text-sm text-gray-500" data-id="element-5571">Contact</p>
                    <p className="font-medium" data-id="element-5572">{selectedPatient.contact}</p>
                  </div>
                  <div data-id="element-5573">
                    <p className="text-sm text-gray-500" data-id="element-5574">Address</p>
                    <p className="font-medium" data-id="element-5575">{selectedPatient.address}</p>
                  </div>
                  <div data-id="element-5576">
                    <p className="text-sm text-gray-500" data-id="element-5577">Next of Kin</p>
                    <p className="font-medium" data-id="element-5578">
                      {selectedPatient.nextOfKin} ({selectedPatient.nokContact})
                    </p>
                  </div>
                  <div data-id="element-5579">
                    <p className="text-sm text-gray-500" data-id="element-5580">Allergies</p>
                    <p className="font-medium text-red-600" data-id="element-5581">
                      {selectedPatient.allergies}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6" data-id="element-5582">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-5583">
                    <Clock className="w-5 h-5 mr-2 text-gray-500" data-id="element-5584" /> Visit
                    History
                  </h4>

                  {getPatientEncounters(selectedPatient.id).length > 0 ? <div className="space-y-3" data-id="element-5585">
                      {getPatientEncounters(selectedPatient.id).map(enc => <div key={enc.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex justify-between items-center" data-id="element-5586">
                          <div data-id="element-5587">
                            <p className="font-medium text-gray-900" data-id="element-5588">
                              {new Date(enc.startedAt).toLocaleDateString()} at{' '}
                              {new Date(enc.startedAt).toLocaleTimeString()}
                            </p>
                            <p className="text-sm text-gray-500" data-id="element-5589">
                              {enc.lastAttendingClinician ? `Seen by ${enc.lastAttendingClinician}` : 'No clinician assigned'}
                            </p>
                          </div>
                          <Badge variant={enc.stage === 'COMPLETED' ? 'success' : 'warning'} data-id="element-5590">
                            {enc.stage}
                          </Badge>
                        </div>)}
                    </div> : <p className="text-gray-500 italic" data-id="element-5591">
                      No previous visits found.
                    </p>}
                </div>
              </Card>
            </div>}
        </div>}

      {activeTab === 'reports' && <PatientReports data-id="element-5592" />}

      {activeTab === 'reviewDue' && <PatientReviewDueList data-id="element-5593" />}

      {activeTab === 'maternity' && <Card title="Register Maternity Patient" data-id="element-5594">
          <form onSubmit={handleRegisterMaternity} className="space-y-6" data-id="element-5595">
            <div data-id="element-5596">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-5597">
                Patient Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5598">
                <Input label="First Name" required value={maternityForm.firstName} onChange={e => setMaternityForm({
              ...maternityForm,
              firstName: e.target.value
            })} data-id="element-5599" />
                <Input label="Surname" required value={maternityForm.surname} onChange={e => setMaternityForm({
              ...maternityForm,
              surname: e.target.value
            })} data-id="element-5600" />
                <Input label="Other Names" value={maternityForm.otherNames} onChange={e => setMaternityForm({
              ...maternityForm,
              otherNames: e.target.value
            })} data-id="element-5601" />
                <Input label="Age" type="number" required value={maternityForm.age} onChange={e => setMaternityForm({
              ...maternityForm,
              age: e.target.value
            })} data-id="element-5602" />
                <Input label="Phone Number" required value={maternityForm.phone} onChange={e => setMaternityForm({
              ...maternityForm,
              phone: e.target.value
            })} data-id="element-5603" />
                <Input label="Religion" value={maternityForm.religion} onChange={e => setMaternityForm({
              ...maternityForm,
              religion: e.target.value
            })} data-id="element-5604" />
                <Input label="Church Attended" value={maternityForm.churchAttended} onChange={e => setMaternityForm({
              ...maternityForm,
              churchAttended: e.target.value
            })} data-id="element-5605" />
                <div className="md:col-span-2" data-id="element-5606">
                  <Input label="Residential Address" required value={maternityForm.residentialAddress} onChange={e => setMaternityForm({
                ...maternityForm,
                residentialAddress: e.target.value
              })} data-id="element-5607" />
                </div>
                <div className="md:col-span-3" data-id="element-5608">
                  <Input label="Location / Landmark" value={maternityForm.locationLandmark} onChange={e => setMaternityForm({
                ...maternityForm,
                locationLandmark: e.target.value
              })} data-id="element-5609" />
                </div>
              </div>
            </div>

            <div data-id="element-5610">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-5611">
                Next of Kin
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5612">
                <Input label="Name" required value={maternityForm.nokName} onChange={e => setMaternityForm({
              ...maternityForm,
              nokName: e.target.value
            })} data-id="element-5613" />
                <Input label="Phone" required value={maternityForm.nokPhone} onChange={e => setMaternityForm({
              ...maternityForm,
              nokPhone: e.target.value
            })} data-id="element-5614" />
                <Input label="Relationship" required value={maternityForm.nokRelationship} onChange={e => setMaternityForm({
              ...maternityForm,
              nokRelationship: e.target.value
            })} data-id="element-5615" />
              </div>
            </div>

            <div data-id="element-5616">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-5617">
                Spouse Details (Optional)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5618">
                <Input label="First Name" value={maternityForm.spouseFirstName} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseFirstName: e.target.value
            })} data-id="element-5619" />
                <Input label="Surname" value={maternityForm.spouseSurname} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseSurname: e.target.value
            })} data-id="element-5620" />
                <Input label="Other Names" value={maternityForm.spouseOtherNames} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseOtherNames: e.target.value
            })} data-id="element-5621" />
                <Input label="Occupation" value={maternityForm.spouseOccupation} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseOccupation: e.target.value
            })} data-id="element-5622" />
                <Input label="Contact" value={maternityForm.spouseContact} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseContact: e.target.value
            })} data-id="element-5623" />
              </div>
            </div>

            <div className="flex justify-end pt-4" data-id="element-5624">
              <Button type="submit" size="lg" className="bg-pink-600 hover:bg-pink-700 text-white" data-id="element-5625">
                Register & Forward to Nursing
              </Button>
            </div>
          </form>
        </Card>}
    </div>;
};